import { listViewers as listAnnouncementViewers } from "client/Api/Hub/Announcements/announcementViewer";
import { listViewers as listEventViewers } from "client/Api/Hub/Events/eventViewer";
import { listViewers as listKnowledgeItemViewers } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { HubItemViewerListItemKind, HubItemViewerListItemModel } from "client/Api/Hub/common";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	Dialog,
	FormattedText,
	ListItem,
	ListItemTitle,
	LoadMoreIndicator,
	NoResults,
	PersonAvatar
} from "client/Components";
import {
	createRemoteDataSource,
	LazyLoadCursorKind,
	NumberFormatter,
	RemoteDataSourceState,
	useEffectSkipFirstRender,
	useOpenDialog
} from "client/Hooks";
import { formatUnit } from "client/Misc/cultureSpecificUtils";
import {
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { useHubStatisticsFormatter } from "../useHubStatisticsFormatter";
import { UserListItem } from "client/Features/Users/UserListItem/UserListItem";
import styles from "./ViewersDialog.module.less";

export type ViewersDialogProps = {
	itemKind: HubItemKind;
	itemId: number;
	count: number;
	onClose: () => void;
};

export const ViewersDialog = (props: ViewersDialogProps): ReactElement => {
	const {
		itemKind,
		itemId,
		count: initialCount,
		onClose
	} = props;

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [count] = useState<number>(initialCount);

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const {
		currentItems,
		getLoadingState,
		hasLazyLoadError,
		hasMoreOnServer,
		retryToLoadLastPage,
		reload
	} = useDataSource({
		filtering: {
			itemId,
			itemKind
		},
		desiredAmountOfItems
	});

	const increaseAmount = useCallback(
		(): void => {
			setDesiredAmountOfItems(x => x + PAGE_SIZE);
		},
		[]
	);

	useEffectSkipFirstRender(
		(): void => {
			hasImplicitDependencyOn(itemKind, itemId);
			setDesiredAmountOfItems(PAGE_SIZE);
		},
		[itemId, itemKind]
	);

	const renderContent = (): ReactElement | null => {
		const loadingState: RemoteDataSourceState = getLoadingState();

		if (currentItems.length > 0 || loadingState === "loading") {
			return (
				<>
					<List
						items={currentItems}
						hasMoreOnServer={hasMoreOnServer}
						isLoading={loadingState === "loading"}
						onRequestMoreItems={increaseAmount}
						statisticsFormatter={statisticsFormatter}
					/>
					{hasLazyLoadError && (
						<NoResults
							icon="gear"
							title={localize("_UnableToRetrieveViewedByInformation")}
							message={localize("_TheViewedByInformationCouldNotBeRetrieved")}
							onRetry={retryToLoadLastPage}
							hasExtraTopPadding
						/>
					)}
				</>
			);
		}

		return (
			<NoResults
				icon="gear"
				title={localize("_UnableToRetrieveViewedByInformation")}
				message={localize("_TheViewedByInformationCouldNotBeRetrieved")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<Dialog
			height="fill"
			trackingId="viewers-dialog"
		>
			<Dialog.Header
				onClose={onClose}
				title={(
					<FormattedText
						text={localize("_ViewedByAmount")}
						thingsToReplace={[
							{
								what: "{{Amount}}",
								filler: statisticsFormatter(count)
							}
						]}
					/>
				)}
			/>
			<Dialog.Body goesEdgeToEdge>
				{renderContent()}
			</Dialog.Body>
			<Dialog.Footer />
		</Dialog>
	);
};
ViewersDialog.displayName = "ViewersDialog";

export type ViewersDialogData = Omit<ViewersDialogProps, "onClose">;

export function useViewersDialog(): [
	element: ReactElement | null,
	open: (data: ViewersDialogData) => Promise<void>
] {
	const [element, originalOpen] = useOpenDialog<ViewersDialogData>(
		(data, onClose) => (
			<ViewersDialog
				{...data}
				onClose={onClose}
			/>
		)
	);

	return [
		element,
		useCallback(
			(data: ViewersDialogData): Promise<void> => {
				return originalOpen(data);
			},
			[originalOpen]
		)
	];
}


type ListProps = {
	items: readonly HubItemViewerListItemModel[];
	isLoading: boolean;
	hasMoreOnServer: boolean;
	onRequestMoreItems: () => void;
	statisticsFormatter: NumberFormatter;
};

const List = (props: ListProps): ReactElement => {
	const {
		items,
		hasMoreOnServer,
		isLoading,
		onRequestMoreItems,
		statisticsFormatter
	} = props;

	return (
		<ul
			{...setTrackingId("viewers-list")}
			className={styles.list}
		>
			{items.map(
				x => (
					<ViewerListItem
						key={getItemUniqueKey(x)}
						item={x}
						statisticsFormatter={statisticsFormatter}
					/>
				)
			)}
			<li>
				<LoadMoreIndicator
					isLoadingData={isLoading}
					hasMoreItems={hasMoreOnServer}
					onRequestMoreItems={onRequestMoreItems}
				/>
			</li>
		</ul>
	);
};

function getItemUniqueKey(item: HubItemViewerListItemModel): string {
	switch (item.kind) {
		case HubItemViewerListItemKind.User:
			return `user-${item.user.userId}`;
		case HubItemViewerListItemKind.DeletedUsers:
			return "deleted-users";
		default:
			never(item);
	}
}

type ViewerListItemProps = {
	item: HubItemViewerListItemModel;
	statisticsFormatter: NumberFormatter;
};

const ViewerListItem = (props: ViewerListItemProps): ReactElement => {
	const {
		item,
		statisticsFormatter
	} = props;

	switch (item.kind) {
		case HubItemViewerListItemKind.User:
			return (
				<li>
					<UserListItem
						user={item.user}
						trackingId={generateTrackingId("user-item", `|${item.user.userId}`)}
						noMobileBorderBottom
					/>
				</li>
			);
		case HubItemViewerListItemKind.DeletedUsers:
			return (
				<li>
					<ListItem
						trackingId="deleted-users"
						noMobileBorderBottom
						interactionMode="none"
					>
						<ListItem.Content
							thumbnail={(
								<PersonAvatar
									placeholderKind="user-icon"
									size="small"
								/>
							)}
						>
							<ListItemTitle
								trackingId="title"
							>
								{formatUnit({
									value: item.count,
									singularForm: "_1DeletedUser",
									pluralForm: "_NDeletedUsers",
									valueFormatter: statisticsFormatter
								})}
							</ListItemTitle>
						</ListItem.Content>
					</ListItem>
				</li>
			);
		default:
			never(item);
	}
};
ViewerListItem.displayName = "ViewerListItem";

type Filter = {
	itemKind: HubItemKind;
	itemId: number;
};

const useDataSource = createRemoteDataSource<
	HubItemViewerListItemModel,
	Filter
>()
	.setConfiguration({
		lazyLoadCursor: LazyLoadCursorKind.SkipTake,
		hasFiltering: true
	})
	.getHook({
		performRequest: (options): Promise<LazyLoadPageResult<HubItemViewerListItemModel>> => {
			const {
				skip,
				amount,
				signal,
				filterState: {
					itemKind,
					itemId
				}
			} = options;

			switch (itemKind) {
				case HubItemKind.Announcement:
					return listAnnouncementViewers(
						itemId,
						skip,
						amount,
						signal
					);
				case HubItemKind.KnowledgeItem:
					return listKnowledgeItemViewers(
						itemId,
						skip,
						amount,
						signal
					);
				case HubItemKind.Event:
					return listEventViewers(
						itemId,
						skip,
						amount,
						signal
					);
				default:
					never(itemKind);
			}
		}
	});

	const PAGE_SIZE: number = 50;